import { makeAutoObservable, runInAction } from 'mobx';
import { enableStaticRendering } from 'mobx-react-lite';
import { Session } from 'next-auth';
import { PluginUser } from '../../components/plugins/types';
import { DetailedUser, Group, Organization, roles } from '../../model/types';
import { getUserById } from '../../services/people/userServices';
import { isSSR } from '../../utils/helpers';

enableStaticRendering(isSSR());

const userInfoKeys = {
    email: 'email',
    fullName: 'fullName',
    // id: 'id',
    language: 'language',
    phone: 'phone',
    roles: 'roles',
    username: 'username'
} as const;

type userInfoKey = keyof typeof userInfoKeys;

// TODO cagri: should extend with about language, organization, groups info
export class CurrentUserStore {
    email: string;
    fullName: string;
    id: number;
    phone: string;
    role: roles;
    username: typeof this.email; // TODO: deprecate and use email instead

    // optional
    groups?: Group[];
    organization?: Organization & { users: string[] };
    tags?: string[];
    profilePicture?: string;
    constructor() {
        this.email = null;
        this.fullName = null;
        this.id = null;
        this.phone = null;
        this.role = null;
        this.username = null;
        this.groups = [];
        this.organization = {} as CurrentUserStore['organization'];
        this.tags = [];
        this.profilePicture = null;
        makeAutoObservable(this);
    }

    toPluginUser(): PluginUser {
        return {
            id: this.id,
            fullName: this.fullName,
            email: this.email,
            phone: this.phone
        };
    }

    chatUsername = () => {
        return this.fullName.split(' ')[0];
    };

    updateCurrentUserEmail = (email: typeof this.email): void => {
        this.email = email;
        this.username = email;
    };

    updateCurrentUserUsername = (username: typeof this.username): void => {
        this.email = username;
        this.username = username;
    };
    updateCurrentUserFullName = (fullName: typeof this.fullName): void => {
        this.fullName = fullName;
    };
    updateCurrentUserRole = (role: typeof this.role): void => {
        this.role = role;
    };

    updateCurrentUserPhone = (phone: typeof this.phone): void => {
        this.phone = phone;
    };

    updateCurrentUserInfo = (user: DetailedUser, infoKeyToUpdate: userInfoKey): void => {
        if (!user) {
            // console.debug('CurrentUserStore updateCurrentUserInfo: user is empty, cannot update store');
            return;
        }
        if (infoKeyToUpdate === 'email' || infoKeyToUpdate === 'username') {
            const value = user?.email;

            this.updateCurrentUserEmail(value);
            this.updateCurrentUserUsername(value);

            return;
        }

        if (infoKeyToUpdate === 'fullName') {
            this.updateCurrentUserFullName(user.fullName);

            return;
        }
        if (infoKeyToUpdate === 'phone') {
            this.updateCurrentUserPhone(user.phone);

            return;
        }

        if (infoKeyToUpdate === 'roles') {
            this.updateCurrentUserRole(user.role?.authority);

            return;
        }
    };

    refreshStore = async (userId: number): Promise<void> => {
        const currentUser = await getUserById(userId);
        runInAction(() => {
            this.email = currentUser?.email;
            this.fullName = currentUser?.fullName;
            this.id = currentUser?.id;
            this.phone = currentUser?.phone;
            this.role = currentUser?.role?.authority;
            this.username = currentUser?.email;
            this.groups = currentUser?.groups;
            this.organization = currentUser?.organization;
            this.tags = currentUser?.tags;
            this.profilePicture = currentUser?.profilePicture;
        });
    };

    hydrate = async (session: Session): Promise<void> => {
        const {
            userInfo: { username, userId, rls }
        } = session;

        this.email = username;
        this.id = userId;
        this.role = rls[0];
        this.username = username;

        if (this.id) {
            const currentUser = await getUserById(userId);
            runInAction(() => {
                this.email = currentUser?.email;
                this.fullName = currentUser?.fullName;
                this.id = currentUser?.id;
                this.phone = currentUser?.phone;
                this.role = currentUser?.role?.authority;
                this.username = currentUser?.email;
                this.groups = currentUser?.groups;
                this.organization = currentUser?.organization;
                this.tags = currentUser?.tags;
                this.profilePicture = currentUser?.profilePicture;
            });
        }
    };
}
